import { useState, useEffect, useRef, useCallback } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { knowledgeBaseContent, type KBSection } from "./knowledge-base-content";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (sectionId: string) => void;
  className?: string;
}

interface SearchResult {
  section: KBSection;
  matchType: "title" | "keyword";
  matchText: string;
}

export function SearchInput({
  value,
  onChange,
  onSelect,
  className,
}: SearchInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Search function
  const search = useCallback((query: string): SearchResult[] => {
    if (!query.trim()) return [];

    const normalizedQuery = query.toLowerCase().trim();
    const searchResults: SearchResult[] = [];

    knowledgeBaseContent.forEach((section) => {
      // Check title match
      if (section.title.toLowerCase().includes(normalizedQuery)) {
        searchResults.push({
          section,
          matchType: "title",
          matchText: section.title,
        });
        return;
      }

      // Check keyword matches
      const matchedKeyword = section.keywords.find((keyword) =>
        keyword.toLowerCase().includes(normalizedQuery)
      );
      if (matchedKeyword) {
        searchResults.push({
          section,
          matchType: "keyword",
          matchText: matchedKeyword,
        });
      }
    });

    return searchResults.slice(0, 8); // Limit results
  }, []);

  // Update results when value changes
  useEffect(() => {
    const searchResults = search(value);
    setResults(searchResults);
    setSelectedIndex(0);
  }, [value, search]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!results.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex].section.id);
        }
        break;
      case "Escape":
        e.preventDefault();
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (sectionId: string) => {
    onSelect?.(sectionId);
    onChange("");
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const showResults = isFocused && value.trim() && results.length > 0;

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search documentation..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Delay to allow click on result
            setTimeout(() => setIsFocused(false), 150);
          }}
          onKeyDown={handleKeyDown}
          className="pl-9 pr-16"
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {value && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onChange("")}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div
          ref={resultsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-80 overflow-auto"
        >
          {results.map((result, index) => {
            const Icon = result.section.icon;
            return (
              <button
                key={result.section.id}
                onClick={() => handleSelect(result.section.id)}
                className={cn(
                  "flex items-center gap-3 w-full px-3 py-2 text-sm text-left transition-colors",
                  index === selectedIndex
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-muted"
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {result.section.title}
                  </div>
                  {result.matchType === "keyword" && (
                    <div className="text-xs text-muted-foreground truncate">
                      Matches: {result.matchText}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* No results message */}
      {isFocused && value.trim() && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 p-4 text-center text-sm text-muted-foreground">
          No results found for "{value}"
        </div>
      )}
    </div>
  );
}

// Helper to filter sections based on search query
export function filterSections(query: string): string[] {
  if (!query.trim()) return knowledgeBaseContent.map((s) => s.id);

  const normalizedQuery = query.toLowerCase().trim();

  return knowledgeBaseContent
    .filter((section) => {
      // Check title
      if (section.title.toLowerCase().includes(normalizedQuery)) return true;
      // Check keywords
      if (section.keywords.some((k) => k.toLowerCase().includes(normalizedQuery))) return true;
      return false;
    })
    .map((s) => s.id);
}
